// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState } from 'react';
import Modal from './Modal';
import { useModules } from '../contexts/ModulesContext';
import { useCredits } from '../contexts/CreditsContext';
import { shipModules } from '../config/ShipModulesConfig';
import Button from './Button';
import './ModulesShop.css';
import { usePurchaseConfirm, PurchaseConfirmBanner } from '../hooks/ui/usePurchaseConfirm';

const ModulesShop = ({ isOpen, onClose, location }) => {
  const { hasModule, canPurchaseModule, purchaseModule, getAvailableModules } = useModules();
  const { credits, removeCredits } = useCredits();
  const { pending, requestPurchase, confirm, cancel } = usePurchaseConfirm();
  
  const [feedback, setFeedback] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const locationType = location.type === 'station' ? 'station' : 'planet';

  const availableModules = getAvailableModules(locationType);
  const installedModulesList = Object.values(shipModules).filter(m => hasModule(m.id));

  // Filter by category
  const filteredModules = selectedCategory === 'all'
    ? availableModules
    : availableModules.filter(m => m.category === selectedCategory);

  const categories = [
    { id: 'all', name: 'All', icon: '📋' },
    { id: 'utility', name: 'Utility', icon: '🔧' },
    { id: 'survival', name: 'Survival', icon: '🛡️' },
    { id: 'exploration', name: 'Exploration', icon: '🔭' }
  ];

  const handlePurchase = (moduleId) => {
    const check = canPurchaseModule(moduleId);
    
    if (!check.canPurchase) {
      if (check.reason === 'Missing required module') {
        const module = shipModules[moduleId];
        const reqModule = shipModules[module.requires];
        showFeedback(`Requires ${reqModule.name} first!`, 'error');
      } else {
        showFeedback(check.reason, 'error');
      }
      return;
    }

    const module = check.module;
    
    if (credits < module.cost) {
      showFeedback(`Insufficient credits! Need ${module.cost} CR.`, 'error');
      return;
    }

    const executePurchase = () => {
      removeCredits(module.cost, `module: ${module.name}`);
      const result = purchaseModule(moduleId);
      if (result.success) {
        showFeedback(`✓ ${module.name} installed!`, 'success');
      }
    };
    requestPurchase(module.name, module.cost, executePurchase);
  };

  const showFeedback = (message, type) => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="⚙️ SHIP MODULES"
      size="large"
      className="modules-shop-modal"
    >
      {/* Info Bar */}
        <div className="modules-shop-info">
          <div className="info-item">
            <span className="info-label">Location:</span>
            <span className="info-value">{location.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Credits:</span>
            <span className="info-value credits">{credits.toLocaleString()} CR</span>
          </div>
          <div className="info-item">
            <span className="info-label">Installed:</span>
            <span className="info-value">{installedModulesList.length} modules</span>
          </div>
        </div>

        {/* Category Filter */}
        <div className="modules-categories">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant="ghost"
              size="small"
              className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span className="cat-icon">{cat.icon}</span>
              <span className="cat-name">{cat.name}</span>
            </Button>
          ))}
        </div>

        {/* Feedback */}
        <PurchaseConfirmBanner pending={pending} onConfirm={confirm} onCancel={cancel} />
        {feedback && (
          <div className={`modules-feedback ${feedback.type}`}>
            {feedback.message}
          </div>
        )}

        {/* Installed Modules */}
        {installedModulesList.length > 0 ? (
          <div className="installed-modules-section">
            <h3>INSTALLED MODULES</h3>
            <div className="installed-modules-grid">
              {installedModulesList.map(module => (
                <div key={module.id} className="installed-module-card">
                  <span className="module-icon">{module.icon}</span>
                  <span className="module-name">{module.name}</span>
                  <span className="module-status">✓ ACTIVE</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="no-installed-modules">
            <div className="no-modules-icon">⚙️</div>
            <div className="no-modules-text">
              No modules installed yet. Visit any station to purchase and install ship modules.
            </div>
          </div>
        )}

        {/* Available Modules */}
        <div className="modules-list-section">
          <h3>AVAILABLE MODULES</h3>
          <div className="modules-list">
            {filteredModules.length === 0 ? (
              <div className="no-modules">
                <div className="no-modules-icon">🚫</div>
                <div className="no-modules-text">
                  {availableModules.length === 0 
                    ? 'All modules purchased! Visit advanced stations for more.'
                    : 'No modules in this category.'}
                </div>
              </div>
            ) : (
              filteredModules.map(module => {
                const check = canPurchaseModule(module.id);
                const canAfford = credits >= module.cost;
                
                return (
                  <div key={module.id} className="module-card">
                    <div className="module-card-header">
                      <div className="module-icon-container">
                        <img 
                          src={`/icons/modules/${module.id.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')}.svg`}
                          alt={module.name}
                          className="module-icon-svg"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <span className="module-icon-emoji">{module.icon}</span>
                      </div>
                      <div className="module-title-section">
                        <div className="module-name-row">
                          <span className="module-name">{module.name}</span>
                          <span className={`module-tier tier-${module.tier}`}>
                            TIER {module.tier}
                          </span>
                        </div>
                        <div className="module-category">{module.category.toUpperCase()}</div>
                      </div>
                    </div>

                    <div className="module-description">{module.description}</div>

                    {/* Effects */}
                    <div className="module-effects">
                      {module.effects.cargoCapacity && (
                        <div className="effect">
                          <span className="effect-label">Cargo Capacity:</span>
                          <span className="effect-value">+{module.effects.cargoCapacity} units</span>
                        </div>
                      )}
                      {module.effects.cargoCapacityMultiplier && (
                        <div className="effect">
                          <span className="effect-label">Cargo Multiplier:</span>
                          <span className="effect-value">×{module.effects.cargoCapacityMultiplier}</span>
                        </div>
                      )}
                      {module.effects.repairRate && (
                        <div className="effect">
                          <span className="effect-label">Auto-Repair:</span>
                          <span className="effect-value">{module.effects.repairRate} HP/10s</span>
                        </div>
                      )}
                      {module.effects.scanBonus && (
                        <div className="effect">
                          <span className="effect-label">Scan Bonus:</span>
                          <span className="effect-value">+{(module.effects.scanBonus * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {module.effects.detectHiddenEvents && (
                        <div className="effect">
                          <span className="effect-label">Special:</span>
                          <span className="effect-value">Detect Hidden Events</span>
                        </div>
                      )}
                      {module.effects.enableMining && (
                        <div className="effect">
                          <span className="effect-label">Special:</span>
                          <span className="effect-value">Enable Mining</span>
                        </div>
                      )}
                      {module.effects.miningBonus && (
                        <div className="effect">
                          <span className="effect-label">Mining Bonus:</span>
                          <span className="effect-value">+{(module.effects.miningBonus * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {module.effects.combatAvoidance && (
                        <div className="effect">
                          <span className="effect-label">Combat Avoidance:</span>
                          <span className="effect-value">{(module.effects.combatAvoidance * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="module-card-footer">
                      <div className="module-cost">
                        <span className="cost-icon">💰</span>
                        <span className="cost-amount">{module.cost.toLocaleString()}</span>
                        <span className="cost-label">credits</span>
                      </div>

                      <Button
                        variant={canAfford && check.canPurchase ? 'success' : 'secondary'}
                        size="small"
                        className="purchase-btn"
                        onClick={() => handlePurchase(module.id)}
                        disabled={!canAfford || !check.canPurchase}
                      >
                        {!check.canPurchase 
                          ? check.reason.toUpperCase()
                          : !canAfford 
                          ? 'INSUFFICIENT CREDITS'
                          : 'INSTALL MODULE'}
                      </Button>
                    </div>

                    {module.requires && !hasModule(module.requires) && (
                      <div className="module-requirement">
                        Requires: {shipModules[module.requires].name}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </Modal>
  );
};

export default ModulesShop;
